import React, { useState, useEffect, useRef } from 'react';
import { Send, Car, Bot, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { chatbotService } from '../services/api';
import { toast } from '../hooks/use-toast';
import { carBrands, carModels } from '../mock/mockData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WELCOME_MESSAGE = "Hi! I'm your AI Car Expert. What's wrong with your car? Describe it in your own words — I'll take it from there.";

const ChatbotSection = () => {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: WELCOME_MESSAGE, timestamp: new Date() }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [vehicleProvided, setVehicleProvided] = useState(false);
  const [pendingMessage, setPendingMessage] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [vehicleInfo, setVehicleInfo] = useState({ brand: '', model: '', year: '', fuel_type: '' });
  const [availableBrands, setAvailableBrands] = useState(carBrands);
  const [availableModels, setAvailableModels] = useState([]);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const suggestionChips = ['Engine noise', 'AC not cooling', 'Brake issue', 'Starting trouble', 'Service due'];

  useEffect(() => {
    fetchVehicles();
    let existingSessionId = sessionStorage.getItem('chatbot_embedded_session_id');
    if (!existingSessionId) {
      existingSessionId = `embedded_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('chatbot_embedded_session_id', existingSessionId);
    }
    setSessionId(existingSessionId);
    loadConversation(existingSessionId);
  }, []);

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const fetchVehicles = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/vehicles`);
      const data = await response.json();
      const vehicles = data.vehicles || [];
      const brands = [...new Set(vehicles.map(v => v.brand))].sort();
      setAvailableBrands(brands);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  useEffect(() => {
    if (vehicleInfo.brand) {
      const fetchModels = async () => {
        try {
          const response = await fetch(`${BACKEND_URL}/api/vehicles`);
          const data = await response.json();
          const vehicles = data.vehicles || [];
          const models = vehicles
            .filter(v => v.brand === vehicleInfo.brand)
            .map(v => v.model)
            .filter((model, index, self) => self.indexOf(model) === index)
            .sort();
          setAvailableModels(models);
        } catch (error) {
          setAvailableModels(carModels[vehicleInfo.brand] || []);
        }
      };
      fetchModels();
    }
  }, [vehicleInfo.brand]);

  const loadConversation = async (sid) => {
    try {
      const response = await chatbotService.getConversation(sid);
      if (response.conversation && response.conversation.messages && response.conversation.messages.length > 0) {
        const formattedMessages = response.conversation.messages.map(msg => ({
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp
        }));
        setMessages(formattedMessages);
        if (response.conversation.vehicle_info && response.conversation.vehicle_info.brand) {
          setVehicleProvided(true);
          setVehicleInfo(response.conversation.vehicle_info);
          setIsExpanded(true);
        }
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  const callAIWithMessage = async (message, vInfo) => {
    setIsLoading(true);
    try {
      const response = await chatbotService.sendMessage({
        session_id: sessionId,
        message,
        vehicle_brand: vInfo.brand,
        vehicle_model: vInfo.model,
        vehicle_year: vInfo.year ? parseInt(vInfo.year) : null,
        vehicle_fuel_type: vInfo.fuel_type
      });

      const aiMessage = {
        role: 'assistant',
        content: response.message,
        timestamp: new Date(),
        showBookingButton: response.show_booking_button,
        recommendedService: response.recommended_service
      };
      setMessages(prev => [...prev, aiMessage]);

      if (response.booking_created) {
        toast({ title: '✅ Booking Created!', description: `Booking ID: ${response.booking_id}`, duration: 5000 });
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to send message. Please try again.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (overrideText) => {
    const text = (overrideText ?? inputMessage).trim();
    if (!text) return;

    setMessages(prev => [...prev, { role: 'user', content: text, timestamp: new Date() }]);
    setInputMessage('');
    setIsExpanded(true);

    if (!vehicleProvided) {
      // Store and ask for vehicle info inline
      setPendingMessage(text);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Got it — I can help with that. Quick question: which car is this?",
        timestamp: new Date(),
        inlineVehicleForm: true
      }]);
      return;
    }

    await callAIWithMessage(text, vehicleInfo);
  };

  const handleVehicleFormSubmit = async () => {
    if (!vehicleInfo.brand || !vehicleInfo.model) {
      toast({ title: 'Quick — brand & model?', description: 'Please select your car brand and model to continue', variant: 'destructive' });
      return;
    }
    // Remove the inline form message, add a user message showing the selection
    setMessages(prev => {
      const withoutForm = prev.filter(m => !m.inlineVehicleForm);
      return [
        ...withoutForm,
        {
          role: 'user',
          content: `${vehicleInfo.brand} ${vehicleInfo.model}${vehicleInfo.year ? ` (${vehicleInfo.year})` : ''}${vehicleInfo.fuel_type ? ` · ${vehicleInfo.fuel_type}` : ''}`,
          timestamp: new Date()
        }
      ];
    });
    setVehicleProvided(true);
    // Now call AI with the original pending message
    if (pendingMessage) {
      await callAIWithMessage(pendingMessage, vehicleInfo);
      setPendingMessage('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleChipClick = (chip) => {
    handleSendMessage(chip);
  };

  return (
    <section className="py-6 bg-slate-950">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 rounded-2xl shadow-xl border border-slate-700/50 backdrop-blur-xl overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 flex items-center justify-between border-b border-slate-800/60">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center shadow-[0_0_15px_rgba(6,182,212,0.35)]">
                  <Bot className="w-5 h-5 text-slate-950" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg flex items-center gap-2">
                    AI Car Expert
                    <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse shadow-[0_0_8px_rgba(34,211,238,0.8)]"></span>
                  </h3>
                  <p className="text-slate-400 text-xs">Instant diagnosis · 24/7 available</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {vehicleProvided && (
                  <div className="hidden sm:flex items-center gap-2 px-3 py-1 rounded-full bg-slate-950/60 border border-slate-700/50">
                    <Car className="w-3.5 h-3.5 text-cyan-400" />
                    <span className="text-xs text-slate-300 font-medium">{vehicleInfo.brand} {vehicleInfo.model}</span>
                  </div>
                )}
                <span className="text-xs text-cyan-300 bg-cyan-500/10 border border-cyan-500/20 px-3 py-1 rounded-full font-medium">Free</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsExpanded(!isExpanded)}
                  data-testid="chatbot-expand-toggle"
                  className="text-xs text-slate-400 hover:text-cyan-400 hover:bg-slate-800/60"
                >
                  {isExpanded ? 'Minimize' : 'Expand'}
                  <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </Button>
              </div>
            </div>

            {/* Messages */}
            <div
              ref={messagesContainerRef}
              className={`overflow-y-auto px-4 py-4 space-y-3 transition-all duration-300 bg-slate-950/40 ${isExpanded ? 'h-[340px]' : 'h-[160px]'}`}
              data-testid="chatbot-messages"
            >
              {messages.map((msg, index) => (
                <div key={index}>
                  <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                      msg.role === 'user'
                        ? 'bg-cyan-500 text-slate-950 font-medium rounded-br-md'
                        : 'bg-slate-900/80 text-slate-200 border border-slate-700/50 rounded-bl-md'
                    }`}>
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>

                  {/* Inline vehicle mini-form inside a bot bubble */}
                  {msg.inlineVehicleForm && (
                    <div className="mt-2 ml-0 max-w-[85%] bg-slate-950/60 border border-cyan-500/20 rounded-2xl rounded-bl-md p-3" data-testid="inline-vehicle-form">
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <Select
                          value={vehicleInfo.brand}
                          onValueChange={(value) => setVehicleInfo({ ...vehicleInfo, brand: value, model: '' })}
                        >
                          <SelectTrigger className="bg-slate-900/80 border-slate-700 text-white h-9 text-xs hover:border-cyan-500/40" data-testid="vehicle-brand-select">
                            <SelectValue placeholder="Brand *" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-700 max-h-[240px]">
                            {availableBrands.map(brand => (
                              <SelectItem key={brand} value={brand} className="text-slate-200 focus:bg-slate-800 focus:text-white text-xs">{brand}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={vehicleInfo.model}
                          onValueChange={(value) => setVehicleInfo({ ...vehicleInfo, model: value })}
                          disabled={!vehicleInfo.brand}
                        >
                          <SelectTrigger className="bg-slate-900/80 border-slate-700 text-white h-9 text-xs hover:border-cyan-500/40 disabled:opacity-50" data-testid="vehicle-model-select">
                            <SelectValue placeholder="Model *" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-700 max-h-[240px]">
                            {availableModels.map(model => (
                              <SelectItem key={model} value={model} className="text-slate-200 focus:bg-slate-800 focus:text-white text-xs">{model}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        onClick={handleVehicleFormSubmit}
                        size="sm"
                        data-testid="vehicle-continue-btn"
                        className="w-full h-8 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold text-xs rounded-md shadow-[0_0_12px_rgba(6,182,212,0.2)]"
                      >
                        Continue
                      </Button>
                    </div>
                  )}

                  {/* Booking button on AI recommendations */}
                  {msg.role === 'assistant' && msg.showBookingButton && (
                    <div className="flex justify-start mt-2">
                      <Button
                        onClick={() => {
                          if (msg.recommendedService) sessionStorage.setItem('recommended_service', msg.recommendedService);
                          sessionStorage.setItem('chatbot_vehicle_info', JSON.stringify(vehicleInfo));
                          window.location.href = '/services';
                        }}
                        size="sm"
                        className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-xs font-semibold"
                      >
                        Book Service
                      </Button>
                    </div>
                  )}
                </div>
              ))}

              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-slate-900/80 border border-slate-700/50 rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex gap-1.5">
                      <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></div>
                      <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Suggestion Chips */}
            {messages.length <= 1 && (
              <div className="px-4 pt-3 pb-2 flex flex-wrap gap-2" data-testid="chatbot-suggestions">
                {suggestionChips.map((chip) => (
                  <button
                    key={chip}
                    onClick={() => handleChipClick(chip)}
                    data-testid={`chip-${chip.toLowerCase().replace(/\s+/g, '-')}`}
                    className="group px-3 py-1.5 text-xs text-slate-300 bg-slate-900/60 border border-slate-700/60 rounded-full hover:border-cyan-500/50 hover:text-cyan-300 hover:bg-slate-900/90 transition-colors duration-200"
                  >
                    {chip}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <div className="p-3 bg-slate-900/60 border-t border-slate-800">
              <div className="flex gap-2">
                <Input
                  placeholder={vehicleProvided ? 'Tell me more...' : "What's wrong with your car?"}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={isLoading}
                  data-testid="chatbot-input"
                  className="flex-1 h-10 text-sm bg-slate-950/60 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
                <Button
                  onClick={() => handleSendMessage()}
                  disabled={isLoading || !inputMessage.trim()}
                  data-testid="chatbot-send-btn"
                  className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 h-10 px-4 disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ChatbotSection;
